import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { courseAPI, uploadAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Play, FileText, Loader2, ArrowLeft, BookOpen, Video, Upload, Eye, Globe, Lock, Save, ChevronRight } from 'lucide-react';

export default function CourseBuilderPage() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(!!courseId);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [activeLesson, setActiveLesson] = useState(null);

  // Load course if editing
  useEffect(() => {
    if (courseId) {
      courseAPI.get(courseId).then(r => { setCourse(r.data); setLoading(false); }).catch(() => { toast.error('Course not found'); navigate('/app/studio'); });
    } else {
      setCourse({ title: '', description: '', thumbnail_url: '', modules: [], is_free: false, price: 0, currency: 'MYR' });
      setLoading(false);
    }
  }, [courseId, navigate]);

  const saveCourse = async () => {
    if (!course.title.trim()) { toast.error('Course title required'); return; }
    setSaving(true);
    try {
      if (courseId) {
        await courseAPI.update(courseId, course);
        toast.success('Saved!');
      } else {
        const r = await courseAPI.create(course);
        navigate(`/app/studio/course/${r.data.id}`, { replace: true });
        toast.success('Course created!');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handlePublish = async () => {
    if (!courseId) { toast.error('Save course first'); return; }
    setPublishing(true);
    try {
      await courseAPI.update(courseId, course);
      const r = await courseAPI.publish(courseId);
      setCourse(prev => ({ ...prev, is_published: true, product_id: r.data.product_id }));
      toast.success('Course published! Added to Product Library.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPublishing(false); }
  };

  const handleUnpublish = async () => {
    if (!courseId) return;
    try {
      await courseAPI.unpublish(courseId);
      setCourse(prev => ({ ...prev, is_published: false }));
      toast.success('Course unpublished');
    } catch { toast.error('Failed'); }
  };

  // Module helpers
  const addModule = () => {
    setCourse(prev => ({ ...prev, modules: [...prev.modules, { id: Date.now().toString(36), title: 'New Module', lessons: [] }] }));
  };
  const updateModule = (idx, field, value) => {
    setCourse(prev => { const m = [...prev.modules]; m[idx] = { ...m[idx], [field]: value }; return { ...prev, modules: m }; });
  };
  const deleteModule = (idx) => {
    if (!window.confirm('Delete this module and all its lessons?')) return;
    setCourse(prev => ({ ...prev, modules: prev.modules.filter((_, i) => i !== idx) }));
  };
  const moveModule = (idx, dir) => {
    setCourse(prev => {
      const m = [...prev.modules]; const target = idx + dir;
      if (target < 0 || target >= m.length) return prev;
      [m[idx], m[target]] = [m[target], m[idx]];
      return { ...prev, modules: m };
    });
  };

  // Lesson helpers
  const addLesson = (moduleIdx) => {
    setCourse(prev => {
      const m = [...prev.modules];
      m[moduleIdx] = { ...m[moduleIdx], lessons: [...m[moduleIdx].lessons, { id: Date.now().toString(36), title: 'New Lesson', video_url: '', content: '', file_url: '', file_name: '', duration_mins: 0 }] };
      return { ...prev, modules: m };
    });
    setActiveLesson({ moduleIdx, lessonIdx: course.modules[moduleIdx].lessons.length });
  };
  const updateLesson = (moduleIdx, lessonIdx, field, value) => {
    setCourse(prev => {
      const m = [...prev.modules]; const lessons = [...m[moduleIdx].lessons];
      lessons[lessonIdx] = { ...lessons[lessonIdx], [field]: value };
      m[moduleIdx] = { ...m[moduleIdx], lessons };
      return { ...prev, modules: m };
    });
  };
  const deleteLesson = (moduleIdx, lessonIdx) => {
    setCourse(prev => {
      const m = [...prev.modules];
      m[moduleIdx] = { ...m[moduleIdx], lessons: m[moduleIdx].lessons.filter((_, i) => i !== lessonIdx) };
      return { ...prev, modules: m };
    });
    setActiveLesson(null);
  };
  const moveLesson = (moduleIdx, lessonIdx, dir) => {
    setCourse(prev => {
      const m = [...prev.modules]; const lessons = [...m[moduleIdx].lessons];
      const target = lessonIdx + dir;
      if (target < 0 || target >= lessons.length) return prev;
      [lessons[lessonIdx], lessons[target]] = [lessons[target], lessons[lessonIdx]];
      m[moduleIdx] = { ...m[moduleIdx], lessons };
      return { ...prev, modules: m };
    });
  };

  // File upload for lesson attachment
  const handleFileUpload = async (moduleIdx, lessonIdx, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await uploadAPI.file(fd);
      updateLesson(moduleIdx, lessonIdx, 'file_url', r.data.file_url);
      updateLesson(moduleIdx, lessonIdx, 'file_name', file.name);
      toast.success('File uploaded!');
    } catch { toast.error('Upload failed'); }
    e.target.value = '';
  };

  // Video embed helper
  const getEmbedUrl = (url) => {
    if (!url) return '';
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  const totalLessons = course?.modules?.reduce((s, m) => s + m.lessons.length, 0) || 0;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: '#195259' }} /></div>;
  if (!course) return null;

  const al = activeLesson;
  const activeL = al ? course.modules[al.moduleIdx]?.lessons[al.lessonIdx] : null;

  return (
    <div className="space-y-5 max-w-[1400px]" data-testid="course-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/app/studio')} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2"><BookOpen className="h-5 w-5" style={{ color: '#195259' }} /> Course Builder</h1>
            <p className="text-xs text-muted-foreground">{course.modules.length} modules &middot; {totalLessons} lessons</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            if (courseId) {
              window.open(`/course-preview/${courseId}`, '_blank');
            } else {
              toast.info('Save the course first to preview');
            }
          }} className="gap-1.5" data-testid="preview-btn">
            <Eye className="h-3.5 w-3.5" /> Preview
          </Button>
          <Button variant="outline" size="sm" onClick={saveCourse} disabled={saving} className="gap-1.5" data-testid="save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </Button>
          {course.is_published ? (
            <Button size="sm" variant="outline" onClick={handleUnpublish} className="gap-1.5 text-red-500 border-red-200" data-testid="unpublish-btn">
              <Lock className="h-3.5 w-3.5" /> Unpublish
            </Button>
          ) : (
            <Button size="sm" onClick={handlePublish} disabled={publishing || totalLessons === 0} className="gap-1.5 text-white" style={{ backgroundColor: '#195259' }} data-testid="publish-btn">
              {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />} Publish
            </Button>
          )}
        </div>
      </div>


      {/* Edit Mode */}
      <div className="flex gap-5">
          {/* Left: Course info + Modules */}
          <div className="flex-1 space-y-4">
            {/* Course Details */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm">Course Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input value={course.title} onChange={e => setCourse(p => ({ ...p, title: e.target.value }))} placeholder="Course title" data-testid="course-title" />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Textarea value={course.description} onChange={e => setCourse(p => ({ ...p, description: e.target.value }))} placeholder="What will students learn?" rows={2} data-testid="course-desc" />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Pricing</Label>
                    <div className="flex gap-2 mt-1">
                      <button onClick={() => setCourse(p => ({ ...p, is_free: false }))} className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${!course.is_free ? 'border-[#195259] bg-[#195259] text-white' : 'border-border'}`}>Paid</button>
                      <button onClick={() => setCourse(p => ({ ...p, is_free: true }))} className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${course.is_free ? 'border-[#195259] bg-[#195259] text-white' : 'border-border'}`}>Free</button>
                    </div>
                  </div>
                  {!course.is_free && (
                    <div className="w-32">
                      <Label className="text-xs">Price (RM)</Label>
                      <Input type="number" min="0" step="0.01" value={course.price} onChange={e => setCourse(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} data-testid="course-price" />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Modules */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Modules & Lessons</p>
                <Button size="sm" variant="outline" onClick={addModule} className="gap-1.5 text-xs" data-testid="add-module-btn"><Plus className="h-3 w-3" /> Add Module</Button>
              </div>

              {course.modules.map((mod, mi) => (
                <Card key={mod.id} className="overflow-hidden" data-testid={`module-${mi}`}>
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-accent/30 border-b">
                    <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input value={mod.title} onChange={e => updateModule(mi, 'title', e.target.value)} className="h-8 text-sm font-semibold border-0 bg-transparent p-0 focus-visible:ring-0" />
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => moveModule(mi, -1)} disabled={mi === 0} className="p-1 rounded hover:bg-accent disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => moveModule(mi, 1)} disabled={mi === course.modules.length - 1} className="p-1 rounded hover:bg-accent disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                      <button onClick={() => deleteModule(mi)} className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <CardContent className="p-3 space-y-1">
                    {mod.lessons.map((les, li) => (
                      <div key={les.id}
                        onClick={() => setActiveLesson({ moduleIdx: mi, lessonIdx: li })}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${al?.moduleIdx === mi && al?.lessonIdx === li ? 'bg-[#195259]/10 border border-[#195259]/20' : 'hover:bg-accent'}`}
                        data-testid={`lesson-${mi}-${li}`}>
                        {les.video_url ? <Video className="h-3.5 w-3.5 text-[#FF9A00] shrink-0" /> : <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                        <span className="text-xs flex-1 truncate">{les.title}</span>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <button onClick={e => { e.stopPropagation(); moveLesson(mi, li, -1); }} disabled={li === 0} className="p-0.5 rounded hover:bg-accent disabled:opacity-30"><ChevronUp className="h-3 w-3" /></button>
                          <button onClick={e => { e.stopPropagation(); moveLesson(mi, li, 1); }} disabled={li === mod.lessons.length - 1} className="p-0.5 rounded hover:bg-accent disabled:opacity-30"><ChevronDown className="h-3 w-3" /></button>
                          <button onClick={e => { e.stopPropagation(); deleteLesson(mi, li); }} className="p-0.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-3 w-3" /></button>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addLesson(mi)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent w-full transition-colors" data-testid={`add-lesson-${mi}`}>
                      <Plus className="h-3 w-3" /> Add Lesson
                    </button>
                  </CardContent>
                </Card>
              ))}

              {course.modules.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-10 text-center">
                    <BookOpen className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">No modules yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Add your first module to start building the course</p>
                    <Button size="sm" onClick={addModule} className="mt-3 gap-1.5 text-white" style={{ backgroundColor: '#195259' }}><Plus className="h-3 w-3" /> Add Module</Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Right: Lesson Editor */}
          <div className="w-96 shrink-0">
            {activeL ? (
              <Card className="sticky top-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Edit Lesson</CardTitle>
                    <button onClick={() => setActiveLesson(null)} className="text-xs text-muted-foreground hover:text-foreground">Close</button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Lesson Title</Label>
                    <Input value={activeL.title} onChange={e => updateLesson(al.moduleIdx, al.lessonIdx, 'title', e.target.value)} data-testid="lesson-title" />
                  </div>
                  <div>
                    <Label className="text-xs">Video URL (YouTube / Vimeo)</Label>
                    <Input value={activeL.video_url} onChange={e => updateLesson(al.moduleIdx, al.lessonIdx, 'video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid="lesson-video" />
                    {activeL.video_url && getEmbedUrl(activeL.video_url) && (
                      <div className="mt-2 rounded-lg overflow-hidden bg-black aspect-video">
                        <iframe src={getEmbedUrl(activeL.video_url)} className="w-full h-full" allowFullScreen title="Preview" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Content</Label>
                    <Textarea value={activeL.content} onChange={e => updateLesson(al.moduleIdx, al.lessonIdx, 'content', e.target.value)} placeholder="Lesson notes, descriptions, key takeaways..." rows={6} data-testid="lesson-content" />
                  </div>
                  <div>
                    <Label className="text-xs">File Attachment</Label>
                    {activeL.file_url ? (
                      <div className="flex items-center gap-2 p-2 rounded-lg border mt-1">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs flex-1 truncate">{activeL.file_name}</span>
                        <button onClick={() => { updateLesson(al.moduleIdx, al.lessonIdx, 'file_url', ''); updateLesson(al.moduleIdx, al.lessonIdx, 'file_name', ''); }} className="text-red-500"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 p-3 rounded-lg border border-dashed mt-1 cursor-pointer hover:bg-accent/30 transition-colors">
                        <input type="file" className="hidden" onChange={e => handleFileUpload(al.moduleIdx, al.lessonIdx, e)} />
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Upload PDF, worksheet, etc.</span>
                      </label>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Duration (minutes)</Label>
                    <Input type="number" min="0" value={activeL.duration_mins} onChange={e => updateLesson(al.moduleIdx, al.lessonIdx, 'duration_mins', parseInt(e.target.value) || 0)} data-testid="lesson-duration" />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-16 text-center">
                  <Play className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">Click a lesson to edit</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
    </div>
  );
}
